import {
  CaretDownIcon,
  CaretLeftIcon,
  CaretUpIcon,
  CodeIcon,
  DownloadIcon,
  EnvelopeIcon,
  GlobeIcon,
  GlobeXIcon,
  PlayIcon,
  SignInIcon,
  StopIcon,
  TrashIcon,
  WarningDiamondIcon,
} from '@phosphor-icons/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, type Track, type User } from './api.ts'
import { useAuth } from './AuthContext.tsx'
import { BytecodeCompiler } from './bytecode-compiler.ts'
import { dspscript } from './dsp-script.ts'
import { NavBar } from './NavBar.tsx'
import { ProfileImage } from './ProfileImage.tsx'
import { Link, navigate } from './Router.tsx'
import { ScrollingVisualizer } from './ScrollingVisualizer.tsx'
import { Spinner } from './Spinner.tsx'
import { useLatencyHintStore, useNavBarHeightStore, useSessionStore } from './store.ts'
import { formatRelativeTime } from './util.ts'

type SortColumn = 'createdAt' | 'lastSeenAt' | null
type SortDirection = 'asc' | 'desc'
type AdminTab = 'users' | 'tracks'

export function Admin() {
  const { user, refreshUser } = useAuth()
  const [activeTab, setActiveTab] = useState<AdminTab>('users')
  const [users, setUsers] = useState<User[]>([])
  const [tracks, setTracks] = useState<Track[]>([])
  const [tracksCursor, setTracksCursor] = useState<string | undefined>(undefined)
  const [hasMoreTracks, setHasMoreTracks] = useState(true)
  const [loading, setLoading] = useState(true)
  const [loadingTracks, setLoadingTracks] = useState(false)
  const [loadingMoreTracks, setLoadingMoreTracks] = useState(false)
  const [error, setError] = useState('')
  const [sendingEmail, setSendingEmail] = useState<string | null>(null)
  const [loggingInAs, setLoggingInAs] = useState<string | null>(null)
  const [deletingUser, setDeletingUser] = useState<string | null>(null)
  const [deletingTrack, setDeletingTrack] = useState<string | null>(null)
  const [playingTrackId, setPlayingTrackId] = useState<string | null>(null)
  const [downloadingBackup, setDownloadingBackup] = useState(false)
  const [activeTrackId, setActiveTrackId] = useState<string | null>(null)
  const [playedTrackIds, setPlayedTrackIds] = useState<Set<string>>(new Set())
  const [trackErrors, setTrackErrors] = useState<Map<string, string | null>>(new Map())
  const [deletedTrackIds, setDeletedTrackIds] = useState<Set<string>>(new Set())
  const [isVerySmallScreen, setIsVerySmallScreen] = useState(false)
  const [sortColumn, setSortColumn] = useState<SortColumn>('createdAt')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const { height: navBarHeight } = useNavBarHeightStore()
  const latencyHint = useLatencyHintStore(state => state.latencyHint)
  const compilerRef = useRef(new BytecodeCompiler())
  const loaderElementRef = useRef<HTMLDivElement | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const hasInitialLoadRef = useRef(false)

  useEffect(() => {
    if (!user) {
      setError('Not authenticated')
      setLoading(false)
      return
    }

    if (!user.isAdmin) {
      setError('Access denied. Admin only.')
      setLoading(false)
      return
    }

    if (activeTab === 'users') {
      const loadUsers = async () => {
        try {
          setLoading(true)
          const response = await api.users.getAllUsers()
          setUsers(response.users)
          setError('')
        }
        catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to load users')
        }
        finally {
          setLoading(false)
        }
      }

      loadUsers()
    }
    else if (activeTab === 'tracks') {
      const loadTracks = async () => {
        try {
          setLoadingTracks(true)
          hasInitialLoadRef.current = false
          const response = await api.admin.getAllTracks(20)
          setTracks(response.tracks)
          setTracksCursor(response.cursor)
          setHasMoreTracks(!!response.cursor)
          setError('')
          hasInitialLoadRef.current = true
        }
        catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to load loops')
        }
        finally {
          setLoadingTracks(false)
        }
      }

      loadTracks()
    }
  }, [user, activeTab])

  useEffect(() => {
    const checkScreenSize = () => {
      setIsVerySmallScreen(window.innerWidth < 700)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  const handleSendWelcomeEmail = async (userId: string) => {
    try {
      setSendingEmail(userId)
      await api.users.sendWelcomeEmail(userId)
      setUsers(prevUsers => prevUsers.map(u => (u.id === userId ? { ...u, sentWelcomeEmail: true } : u)))
    }
    catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send welcome email')
    }
    finally {
      setSendingEmail(null)
    }
  }

  const handleLoginAsUser = async (userId: string) => {
    try {
      setLoggingInAs(userId)
      setError('')
      await api.auth.loginAsUser(userId)
      await refreshUser()
    }
    catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to login as user')
    }
    finally {
      setLoggingInAs(null)
    }
  }

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return
    }

    try {
      setDeletingUser(userId)
      setError('')
      await api.users.deleteUser(userId)
      setUsers(prevUsers => prevUsers.filter(u => u.id !== userId))
    }
    catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user')
    }
    finally {
      setDeletingUser(null)
    }
  }

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    }
    else {
      setSortColumn(column)
      setSortDirection('desc')
    }
  }

  const sortedUsers = useMemo(() => {
    if (!sortColumn) return users

    return [...users].sort((a, b) => {
      const aValue = a[sortColumn] ?? 0
      const bValue = b[sortColumn] ?? 0
      const comparison = aValue - bValue
      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [users, sortColumn, sortDirection])

  const loadMoreTracks = useCallback(async () => {
    if (loadingMoreTracks || !hasMoreTracks || !tracksCursor) return

    try {
      setLoadingMoreTracks(true)
      const response = await api.admin.getAllTracks(20, tracksCursor)
      setTracks(prev => [...prev, ...response.tracks])
      setTracksCursor(response.cursor)
      setHasMoreTracks(!!response.cursor)
    }
    catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more loops')
    }
    finally {
      setLoadingMoreTracks(false)
    }
  }, [loadingMoreTracks, hasMoreTracks, tracksCursor])

  const handlePlayTrack = useCallback(async (track: Track) => {
    try {
      setActiveTrackId(track.id)
      setError('')

      try {
        compilerRef.current.compile(track.code)
        setTrackErrors(prev => {
          const next = new Map(prev)
          next.set(track.id, null)
          return next
        })
      }
      catch (compileErr: any) {
        setTrackErrors(prev => {
          const next = new Map(prev)
          next.set(track.id, compileErr.message || 'Compilation error')
          return next
        })
        setActiveTrackId(null)
        return
      }

      if (!dspscript.getAudioContext()) {
        await dspscript.init(latencyHint)
      }

      await dspscript.loadNewProgram(track.code)
      await dspscript.start()
      setPlayingTrackId(track.id)
      setPlayedTrackIds(prev => new Set(prev).add(track.id))
    }
    catch (err: any) {
      setTrackErrors(prev => {
        const next = new Map(prev)
        next.set(track.id, err.message || 'Error')
        return next
      })
      setActiveTrackId(null)
    }
  }, [latencyHint])

  const handleStopTrack = useCallback(async () => {
    try {
      await dspscript.stop()
      setPlayingTrackId(null)
      setActiveTrackId(null)
    }
    catch (err: any) {
      setError(err.message || 'Failed to stop loop')
    }
  }, [])

  const handleLoadTrackInEditor = useCallback((track: Track) => {
    useSessionStore.getState().setRemixTrack({
      code: track.code,
      trackId: track.id,
      title: track.title,
      artist: track.userName || 'Unknown',
      wasPlaying: playingTrackId === track.id && dspscript.isPlaying(),
    })
    useSessionStore.getState().setSkipExampleOnce(true)
    navigate('/editor')
  }, [playingTrackId])

  const handleDeleteTrack = useCallback(async (trackId: string) => {
    if (deletedTrackIds.has(trackId)) {
      return
    }

    if (!confirm('Are you sure you want to delete this track? This action cannot be undone.')) {
      return
    }

    try {
      setDeletingTrack(trackId)
      setError('')
      await api.tracks.delete(trackId)
      setDeletedTrackIds(prev => new Set(prev).add(trackId))
      setTracks(prev => prev.map(t => t.id === trackId ? { ...t, createdAt: Date.now() } : t))
    }
    catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete loop')
    }
    finally {
      setDeletingTrack(null)
    }
  }, [deletedTrackIds])

  const handleDownloadBackup = useCallback(async () => {
    try {
      setDownloadingBackup(true)
      setError('')
      const blob = await api.admin.downloadBackup()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `backup-${new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)}.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
    catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download backup')
    }
    finally {
      setDownloadingBackup(false)
    }
  }, [])

  const loaderRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (loaderElementRef.current && observerRef.current) {
        observerRef.current.unobserve(loaderElementRef.current)
      }

      loaderElementRef.current = element

      if (!element) {
        return
      }

      const findScrollContainer = (el: HTMLElement): HTMLElement | null => {
        let current: HTMLElement | null = el.parentElement
        while (current) {
          const style = window.getComputedStyle(current)
          if (
            style.overflowY === 'auto'
            || style.overflowY === 'scroll'
            || style.overflow === 'auto'
            || style.overflow === 'scroll'
          ) {
            return current
          }
          current = current.parentElement
        }
        return null
      }

      const scrollContainer = findScrollContainer(element)

      if (observerRef.current) {
        observerRef.current.disconnect()
      }

      observerRef.current = new IntersectionObserver(
        entries => {
          const entry = entries[0]
          if (!hasInitialLoadRef.current) return
          if (entry.isIntersecting && hasMoreTracks && !loadingMoreTracks) {
            loadMoreTracks()
          }
        },
        { rootMargin: '200px', root: scrollContainer },
      )

      observerRef.current.observe(element)
    },
    [hasMoreTracks, loadingMoreTracks, loadMoreTracks],
  )

  useEffect(() => {
    if (hasInitialLoadRef.current && loaderElementRef.current && observerRef.current) {
      observerRef.current.observe(loaderElementRef.current)
    }
  }, [hasInitialLoadRef.current])

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
    }
  }, [])

  const rightSideButtons = (
    <>
      <Link
        href="/"
        className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-lime-400 to-green-600 text-white hover:opacity-90 no-underline flex items-center gap-2"
      >
        <GlobeIcon weight="regular" size={16} />
        <span className="hidden md:inline">Browse</span>
      </Link>
      {!isVerySmallScreen && (
        <Link
          href="/editor"
          className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-orange-500 to-red-600 text-white hover:opacity-90 flex items-center gap-2"
        >
          <CodeIcon weight="regular" size={16} /> Switch to Editor
        </Link>
      )}
    </>
  )

  if (!user) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] text-white">
        <NavBar rightSideButtons={rightSideButtons} />
        <div
          className="flex items-center justify-center"
          style={{ height: `calc(100dvh - ${navBarHeight}px)` }}
        >
          <div className="text-center">
            <div className="text-xl mb-4">Please sign in to access the admin panel</div>
          </div>
        </div>
      </div>
    )
  }

  if (!user.isAdmin) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] text-white">
        <NavBar rightSideButtons={rightSideButtons} />
        <div
          className="flex items-center justify-center"
          style={{ height: `calc(100dvh - ${navBarHeight}px)` }}
        >
          <div className="text-center">
            <div className="text-xl mb-4">Access denied</div>
            <div className="text-[#888]">Admin access required</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen text-white">
      <NavBar rightSideButtons={rightSideButtons} />
      <div style={{ height: `calc(100dvh - ${navBarHeight}px)` }} className="overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold font-[Turret_Road] text-lime-400">
              Admin
            </h1>
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('users')}
                className={`px-4 py-2 rounded-md text-sm font-semibold ${
                  activeTab === 'users'
                    ? 'bg-lime-400 text-black'
                    : 'bg-[#2a2a2a] text-white hover:bg-[#333]'
                }`}
              >
                Users ({!users.length ? '-' : users.length - 2})
              </button>
              <button
                onClick={() => setActiveTab('tracks')}
                className={`px-4 py-2 rounded-md text-sm font-semibold ${
                  activeTab === 'tracks'
                    ? 'bg-lime-400 text-black'
                    : 'bg-[#2a2a2a] text-white hover:bg-[#333]'
                }`}
              >
                Tracks ({tracks.length})
              </button>
              <button
                onClick={handleDownloadBackup}
                disabled={downloadingBackup}
                className="px-4 py-2 rounded-md text-sm font-semibold bg-blue-600 text-white hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <DownloadIcon weight="regular" size={16} />
                {downloadingBackup ? 'Downloading...' : 'Download Backup Zip'}
              </button>
            </div>
          </div>

          {activeTab === 'users' && (loading
            ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-5 h-5">
                  <Spinner />
                </div>
              </div>
            )
            : error
            ? <div className="text-red-400 mb-4">{error}</div>
            : (
              <div className="bg-[#2a2a2a] rounded-lg border border-[#444] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#333] border-b border-[#444]">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">User</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">
                          <button
                            onClick={() => handleSort('createdAt')}
                            className="flex items-center gap-1 hover:text-orange-400"
                          >
                            Created
                            {sortColumn === 'createdAt' && (
                              sortDirection === 'asc'
                                ? <CaretUpIcon size={14} weight="bold" />
                                : <CaretDownIcon size={14} weight="bold" />
                            )}
                          </button>
                        </th>
                        {/* <th className="px-4 py-3 text-left text-sm font-semibold">Role</th> */}
                        <th className="px-4 py-3 text-left text-sm font-semibold">
                          <button
                            onClick={() => handleSort('lastSeenAt')}
                            className="flex items-center gap-1 hover:text-orange-400"
                          >
                            Last Seen
                            {sortColumn === 'lastSeenAt' && (
                              sortDirection === 'asc'
                                ? <CaretUpIcon size={14} weight="bold" />
                                : <CaretDownIcon size={14} weight="bold" />
                            )}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Welcome Email</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedUsers.map(u => (
                        <tr key={u.id} className="border-b border-[#444] hover:bg-[#333]">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <ProfileImage userId={u.id} userName={u.name} size={32} hasAvatar={u.hasAvatar} />
                              <Link
                                href={`/profile/${u.id}`}
                                className="font-medium text-white hover:text-orange-400 cursor-pointer"
                              >
                                {u.name}
                              </Link>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-[#ccc]">{u.email}</td>
                          <td className="px-4 py-3 text-[#888] text-sm">
                            {u.createdAt ? new Date(u.createdAt).toLocaleString() : '-'} (
                            {formatRelativeTime(new Date(u.createdAt || 0))})
                          </td>
                          {
                            /* <td className="px-4 py-3">
                          {u.isAdmin ? (
                            <span className="px-2 py-1 bg-purple-600/20 text-purple-400 rounded text-xs font-semibold">
                              Admin
                            </span>
                          ) : (
                            <span className="text-[#888] text-sm">User</span>
                          )}
                        </td> */
                          }
                          <td className="px-4 py-3 text-[#888] text-sm">
                            {u.lastSeenAt
                              ? (
                                <>
                                  {new Date(u.lastSeenAt).toLocaleString()}{' '}
                                  ({formatRelativeTime(new Date(u.lastSeenAt))})
                                </>
                              )
                              : <span className="text-[#666]">Never</span>}
                          </td>
                          <td className="px-4 py-3">
                            {u.sentWelcomeEmail
                              ? (
                                <span className="px-2 py-1 bg-green-600/20 text-green-400 rounded text-xs font-semibold">
                                  Sent
                                </span>
                              )
                              : (
                                <span className="px-2 py-1 bg-gray-600/20 text-gray-400 rounded text-xs font-semibold">
                                  Not Sent
                                </span>
                              )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleLoginAsUser(u.id)}
                                disabled={loggingInAs === u.id}
                                className="px-3 py-1 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded text-sm font-medium flex items-center gap-1 whitespace-nowrap"
                              >
                                <SignInIcon weight="regular" size={14} />
                                {loggingInAs === u.id ? 'Logging in...' : 'Login as'}
                              </button>
                              <button
                                onClick={() => handleSendWelcomeEmail(u.id)}
                                disabled={sendingEmail === u.id}
                                className="px-3 py-1 bg-neutral-500 hover:bg-orange-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded text-sm font-medium flex items-center gap-1 whitespace-nowrap"
                              >
                                <EnvelopeIcon weight="regular" size={14} />
                                {sendingEmail === u.id ? 'Sending...' : 'Welcome'}
                              </button>
                              <button
                                onClick={() => handleDeleteUser(u.id)}
                                disabled={deletingUser === u.id}
                                className="px-3 py-1 bg-red-600 hover:bg-red-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded text-sm font-medium flex items-center gap-1 whitespace-nowrap"
                              >
                                <TrashIcon weight="regular" size={14} />
                                {deletingUser === u.id ? 'Deleting...' : 'Delete'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {sortedUsers.length === 0 && <div className="px-4 py-8 text-center text-[#888]">No users found</div>}
              </div>
            ))}

          {activeTab === 'tracks' && (
            loadingTracks
              ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-5 h-5">
                    <Spinner />
                  </div>
                </div>
              )
              : error
              ? <div className="text-red-400 mb-4">{error}</div>
              : (
                <div className="bg-[#2a2a2a] rounded-lg border border-[#444] overflow-hidden">
                  {tracks.length === 0
                    ? <div className="px-4 py-8 text-center text-[#888]">No loops found</div>
                    : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-[#333] border-b border-[#444]">
                            <tr>
                              <th className="px-4 py-3 text-left text-sm font-semibold">Play</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold">Loop</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold">User</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold">Visibility</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold">Created</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tracks.map(track => (
                              <tr key={track.id} className="border-b border-[#444] hover:bg-[#333]">
                                <td className="px-4 py-3 relative">
                                  {activeTrackId === track.id && playingTrackId === track.id
                                    ? (
                                      <button
                                        onPointerDown={handleStopTrack}
                                        className="px-2 py-1 bg-gradient-to-br from-neutral-700 to-neutral-900 text-white rounded hover:bg-neutral-800 text-xs flex items-center"
                                        title="Stop"
                                      >
                                        <StopIcon weight="fill" size={12} />
                                      </button>
                                    )
                                    : trackErrors.get(track.id)
                                    ? (
                                      <button
                                        disabled
                                        className="px-2 py-1 bg-transparent text-red-500 rounded cursor-not-allowed text-xs flex items-center"
                                        title={`Loop has error: ${trackErrors.get(track.id)}`}
                                      >
                                        <WarningDiamondIcon weight="regular" size={12} />
                                      </button>
                                    )
                                    : (
                                      <button
                                        onPointerDown={() => handlePlayTrack(track)}
                                        className="px-2 py-1 bg-gradient-to-br from-orange-400 to-red-600 text-white rounded hover:opacity-90 text-xs flex items-center"
                                        title="Play"
                                      >
                                        <PlayIcon weight="fill" size={12} />
                                      </button>
                                    )}
                                </td>
                                <td className="px-4 py-3 relative">
                                  <div className="absolute inset-0 pointer-events-none">
                                    {playedTrackIds.has(track.id) && (
                                      <ScrollingVisualizer
                                        isPlaying={playingTrackId === track.id}
                                        className="w-full h-full opacity-40"
                                        scrollStep={2}
                                      />
                                    )}
                                  </div>
                                  <Link
                                    href={`/loop/${track.id}`}
                                    className={`font-medium hover:text-orange-400 cursor-pointer relative z-10 ${
                                      trackErrors.get(track.id) ? 'text-red-500' : 'text-white'
                                    }`}
                                  >
                                    {track.title}
                                  </Link>
                                </td>
                                <td className="px-4 py-3">
                                  <Link
                                    href={`/profile/${track.userId}`}
                                    className="text-[#888] hover:text-orange-400 cursor-pointer"
                                  >
                                    {track.userName || 'Unknown'}
                                  </Link>
                                </td>
                                <td className="px-4 py-3">
                                  {track.isPublic
                                    ? (
                                      <span className="px-2 py-1 bg-green-600/20 text-green-400 rounded text-xs font-semibold flex items-center gap-1 w-fit">
                                        <GlobeIcon weight="regular" size={12} />
                                        Public
                                      </span>
                                    )
                                    : (
                                      <span className="px-2 py-1 bg-orange-600/20 text-orange-400 rounded text-xs font-semibold flex items-center gap-1 w-fit">
                                        <GlobeXIcon weight="regular" size={12} />
                                        Private
                                      </span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-[#888] text-sm">
                                  {track.createdAt
                                    ? (
                                      <>
                                        {new Date(track.createdAt).toLocaleString()}{' '}
                                        ({formatRelativeTime(new Date(track.createdAt))})
                                      </>
                                    )
                                    : '-'}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleLoadTrackInEditor(track)}
                                      className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded text-sm font-medium flex items-center gap-1"
                                      title="Load in Editor"
                                    >
                                      <CaretLeftIcon weight="regular" size={14} />
                                      <span className="hidden md:inline">Load</span>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteTrack(track.id)}
                                      disabled={deletingTrack === track.id || deletedTrackIds.has(track.id)}
                                      className="px-3 py-1 bg-red-600 hover:bg-red-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded text-sm font-medium flex items-center gap-1"
                                      title={deletedTrackIds.has(track.id) ? 'Already deleted' : 'Delete Loop'}
                                    >
                                      <TrashIcon weight="regular" size={14} />
                                      {deletingTrack === track.id
                                        ? 'Deleting...'
                                        : deletedTrackIds.has(track.id)
                                        ? 'Deleted'
                                        : 'Delete'}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {hasInitialLoadRef.current && hasMoreTracks && (
                          <div
                            ref={loaderRef}
                            className="border-b border-b-[#444] p-4 text-center text-[#888] flex items-center justify-center"
                          >
                            {loadingMoreTracks
                              ? (
                                <div className="w-5 h-5">
                                  <Spinner />
                                </div>
                              )
                              : <div style={{ height: '1px', width: '100%' }} />}
                          </div>
                        )}
                        {hasInitialLoadRef.current && !hasMoreTracks && !loadingMoreTracks && tracks.length > 0 && (
                          <div className="text-center pt-4 pb-5 text-[#888] flex items-center justify-center gap-3">
                            <CodeIcon size={20} />
                          </div>
                        )}
                      </div>
                    )}
                </div>
              )
          )}
        </div>
      </div>
    </div>
  )
}
